import { ORDER_STATUS, statusRank } from './orderConstants';
import { isRetiredOption } from './orderStatus';
import { PAYMENT_STATUS } from '../../../../lib/paymentStatus';

/*
 * The status tab strip.
 *
 * Lifecycle tabs are generated from `admin/statuses`, so a tab can never again
 * be labelled "Delivered" while the rows beneath it read "Completed" — the bug
 * this file used to carry, alongside having no tab at all for Rejected (105),
 * which left those orders reachable only through All.
 *
 * Failed and Abandoned stay hand-written on purpose: they filter
 * `payment_status`, not `status`. They aren't stages an order passes through,
 * they're payment outcomes, and generating them from the status list would be
 * mixing two different axes into one strip. The separator in the UI says so.
 */

/*
 * Header-count keys, which the counts endpoint returns under fixed names rather
 * than per status id. A status with no entry here reports no count instead of
 * guessing — "unknown" and "zero" must not look the same, which is the other
 * half of the bug where a blank chip meant both.
 */
const STATUS_COUNT_KEYS = {
  [ORDER_STATUS.PLACED]: 'order_placed',
  [ORDER_STATUS.CONFIRMED]: 'order_confirmed',
  [ORDER_STATUS.PROCESSING]: 'processing',
  // "ready_order" is the counts endpoint's long-standing key for 102; it kept
  // the name through the rename to "Handed to shipping company".
  [ORDER_STATUS.HANDED_TO_SHIPPING]: 'ready_order',
  [ORDER_STATUS.DELIVERED]: 'delivered',
  [ORDER_STATUS.CANCELLED]: 'cancelled',
  [ORDER_STATUS.RETURNED]: 'returned',
  [ORDER_STATUS.ON_HOLD]: 'on_hold',
};

/*
 * Stable url slugs. Keying tabs by status id would put a bare number in the
 * address bar and break every existing bookmark; keying by the API's title
 * would change the url the day someone renames a status. A status with no slug
 * here falls back to its id, so a new one is still reachable. ("ready" stays
 * the slug for 102 for the same reason: bookmarks outlive renames.)
 */
const STATUS_SLUGS = {
  [ORDER_STATUS.PLACED]: 'placed',
  [ORDER_STATUS.CONFIRMED]: 'confirmed',
  [ORDER_STATUS.PROCESSING]: 'processing',
  [ORDER_STATUS.HANDED_TO_SHIPPING]: 'ready',
  [ORDER_STATUS.DELIVERED]: 'delivered',
  [ORDER_STATUS.CANCELLED]: 'cancelled',
  [ORDER_STATUS.RETURNED]: 'returned',
  [ORDER_STATUS.ON_HOLD]: 'on-hold',
};

export const ALL_TAB = {
  key: 'all',
  label: 'All',
  title: 'All orders',
  kind: 'all',
  queryDefaults: {},
};

export const PAYMENT_TABS = [
  {
    key: 'log',
    label: 'Failed payment',
    title: 'Failed payments',
    kind: 'payment',
    queryDefaults: { payment_status: PAYMENT_STATUS.FAILED },
  },
  {
    key: 'timeout',
    label: 'Abandoned',
    title: 'Abandoned checkouts',
    kind: 'payment',
    queryDefaults: { payment_status: PAYMENT_STATUS.ABANDONED },
  },
];

/**
 * Builds the full tab strip from the API status list.
 *
 * Lifecycle tabs read left to right the way an order moves
 * (STATUS_DISPLAY_ORDER), not by id. A retired status gets no tab: nothing
 * new can land in it, and the handful of old orders that carry it are still
 * reachable through All, with their name intact.
 */
export const buildOrderTabs = (statusOptions = []) => {
  const statusTabs = statusOptions
    .filter((option) => option?.value != null && !isRetiredOption(option))
    .slice()
    .sort((a, b) => statusRank(a.value) - statusRank(b.value))
    .map((option) => {
      const id = Number(option.value);
      return {
        key: STATUS_SLUGS[id] || String(id),
        label: option.label,
        title: option.label,
        kind: 'status',
        statusId: id,
        countKey: STATUS_COUNT_KEYS[id],
        // Every lifecycle tab is payment-filtered to paid, matching the
        // behaviour these tabs have always had.
        queryDefaults: { status: id, payment_status: PAYMENT_STATUS.PAID },
      };
    });

  return [ALL_TAB, ...statusTabs, ...PAYMENT_TABS];
};

export const DEFAULT_ORDER_TAB = ALL_TAB.key;

export const findOrderTab = (tabs, key) =>
  tabs.find((tab) => tab.key === key) || tabs[0] || ALL_TAB;

/**
 * Count for a tab, or null when it genuinely isn't known.
 *
 * All comes from the list response's own pagination total rather than summing
 * the per-status counts: those are each `payment_status: paid` filtered, so an
 * unpaid or rejected order existed in the All *tab* but not in its *count*.
 */
export const getTabCount = ({ tab, headerCount, totalFromPagination }) => {
  if (tab.kind === 'all') {
    return typeof totalFromPagination === 'number' ? totalFromPagination : null;
  }
  if (!tab.countKey) return null;
  const value = headerCount?.[tab.countKey];
  return typeof value === 'number' ? value : null;
};
